import { getBackendUrl } from '../utils/api';
import React, { useState, useEffect } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import storage from '../utils/storage';
import AdminConfigSettings from './AdminConfigSettings';
import AdminSongsAnalyzerAccess from './AdminSongsAnalyzerAccess';
import AdminCompareStationsAccess from './AdminCompareStationsAccess';
import AdminAccessManagement from './AdminAccessManagement';
import ContentQA from './ContentQA';
import DuplicateChecker from './DuplicateChecker';
import DedupDashboard from './DedupDashboard';
import RollbackManager from './RollbackManager';
import SchedulerStatus from './SchedulerStatus';
import SongYearManager from './SongYearManager';
import StationsSyncPanel from './admin/StationsSyncPanel';
import DashboardOverviewWidget from './DashboardOverviewWidget';
import StationTrustDashboard from './StationTrustDashboard';
import SongIdentityManager from './SongIdentityManager';
import BadTrackEraser from './BadTrackEraser';
import AdminMaintenanceTools from './StreamMonitor/AdminMaintenanceTools';
import DuplicateCleanupPanel from './admin/DuplicateCleanupPanel';
import LLMDedupReportPanel from './admin/LLMDedupReportPanel';
import CanonicalMergeJob from './CanonicalMergeJob';
import CanonicalConflictResolver from './CanonicalConflictResolver';
import UserSongsDatabase from './UserSongsDatabase';
import LearningEffectivenessWidget from './LearningEffectivenessWidget';
import StreamUrlSubmissionsPanel from './StreamUrlSubmissionsPanel';

const BACKEND_URL = getBackendUrl();

const AdminDashboard = () => {
  const [dailyStats, setDailyStats] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [geoStats, setGeoStats] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Memory diagnostics state
  const [memoryDiagnostics, setMemoryDiagnostics] = useState(null);
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [showMemoryModal, setShowMemoryModal] = useState(false);

  // v2.13.32 — Dead-code scan export state
  const [deadCodeExporting, setDeadCodeExporting] = useState(false);
  
  // New states
  const [activeTab, setActiveTab] = useState('overview');
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [userList, setUserList] = useState([]);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  
  // Conflict notification badge
  const [conflictCount, setConflictCount] = useState(0);
  const [pendingUrlSubmissions, setPendingUrlSubmissions] = useState(0);
  
  // Search states
  const [userSearch, setUserSearch] = useState('');
  const [analysisSearch, setAnalysisSearch] = useState('');
  const [selectedAnalyses, setSelectedAnalyses] = useState(new Set());
  
  // Modal states for clickable stats
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [modalUsers, setModalUsers] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  
  // Force Regenerate state
  const [forceRegenerateLoading, setForceRegenerateLoading] = useState(false);
  const [forceRegenerateMessage, setForceRegenerateMessage] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Filtered data
  const filteredUsers = userList.filter(user => {
    const search = userSearch.toLowerCase();
    return !search || 
      (user.username || '').toLowerCase().includes(search) ||
      (user.email || '').toLowerCase().includes(search) ||
      (user.station_name || '').toLowerCase().includes(search);
  });
  
  const filteredAnalyses = analysisHistory.filter(analysis => {
    const search = analysisSearch.toLowerCase();
    return !search ||
      (analysis.username || '').toLowerCase().includes(search) ||
      (analysis.email || '').toLowerCase().includes(search) ||
      (analysis.station_name || '').toLowerCase().includes(search) ||
      (analysis.csv_filename || '').toLowerCase().includes(search) ||
      (analysis.analyzer_type || '').toLowerCase().includes(search);
  });
  
  // Toggle selection for bulk delete
  const toggleSelectAnalysis = (analysisId) => {
    setSelectedAnalyses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(analysisId)) {
        newSet.delete(analysisId);
      } else {
        newSet.add(analysisId);
      }
      return newSet;
    });
  };
  
  // Fetch users for modal based on filter type
  const fetchUsersForModal = async (filterType, title) => {
    setModalLoading(true);
    setModalTitle(title);
    setShowUsersModal(true);
    
    try {
      const token = storage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/users-by-filter?filter=${filterType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setModalUsers(data.users || []);
      } else {
        setModalUsers([]);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setModalUsers([]);
    } finally {
      setModalLoading(false);
    }
  };
  
  // Select/deselect all filtered analyses
  const toggleSelectAll = () => {
    if (selectedAnalyses.size === filteredAnalyses.length) {
      setSelectedAnalyses(new Set());
    } else {
      setSelectedAnalyses(new Set(filteredAnalyses.map(a => a._id)));
    }
  };
  
  // Bulk delete selected analyses
  const bulkDeleteAnalyses = async () => {
    if (selectedAnalyses.size === 0) return;
    
    const count = selectedAnalyses.size;
    if (!window.confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε ${count} αρχεί${count === 1 ? 'ο' : 'α'};\n\nΑυτή η ενέργεια δεν μπορεί να αναιρεθεί.`)) {
      return;
    }
    
    try {
      const token = storage.getItem('token');
      let deleted = 0;
      let failed = 0;
      
      for (const analysisId of selectedAnalyses) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/admin/analysis-history/${analysisId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            deleted++;
          } else {
            failed++;
          }
        } catch (err) {
          failed++;
        }
      }
      
      // Update local state
      setAnalysisHistory(prev => prev.filter(a => !selectedAnalyses.has(a._id)));
      setSelectedAnalyses(new Set());
      
      if (failed > 0) {
        alert(`✅ Διαγράφηκαν ${deleted} αρχεία.\n❌ Απέτυχαν ${failed} διαγραφές.`);
      } else {
        alert(`✅ Διαγράφηκαν επιτυχώς ${deleted} αρχεία!`);
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
      alert('❌ Σφάλμα κατά τη μαζική διαγραφή');
    }
  };

  // Fetch conflict badge count
  const fetchConflictCount = async () => {
    try {
      const token = storage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/conflicts/badge-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConflictCount(data.pending_count || 0);
      }
    } catch (err) {
      // Silent fail - badge count is non-critical
    }
  };

  const fetchPendingUrlSubmissions = async () => {
    try {
      const token = storage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/stream-url-submissions/list?status=pending&limit=500`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingUrlSubmissions(data.count || 0);
      }
    } catch {
      // Silent fail - badge count is non-critical
    }
  };

  useEffect(() => {
    fetchAllStats();
    fetchConflictCount();
    fetchPendingUrlSubmissions();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAllStats, 30000);
    // Refresh conflict count every 5 minutes
    const conflictInterval = setInterval(fetchConflictCount, 300000);
    // Refresh pending URL submissions every 2 minutes (admin needs to see them fast)
    const urlSubInterval = setInterval(fetchPendingUrlSubmissions, 120000);
    return () => { clearInterval(interval); clearInterval(conflictInterval); clearInterval(urlSubInterval); };
  }, []);
  
  // Fetch analysis history
  const fetchAnalysisHistory = async () => {
    setAnalysisLoading(true);
    try {
      const token = storage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/analysis-history/list?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAnalysisHistory(data.analyses || []);
    } catch (err) {
      console.error('Failed to fetch analysis history:', err);
    }
    setAnalysisLoading(false);
  };
  
  // Fetch users list
  const fetchUserList = async () => {
    setUsersLoading(true);
    try {
      const token = storage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/users-with-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setUserList(data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
    setUsersLoading(false);
  };
  
  // Fetch memory diagnostics
  const fetchMemoryDiagnostics = async () => {
    setMemoryLoading(true);
    try {
      const token = storage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/dashboard/memory-diagnostics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setMemoryDiagnostics(data);
      setShowMemoryModal(true);
    } catch (err) {
      console.error('Failed to fetch memory diagnostics:', err);
      alert('Failed to fetch memory diagnostics: ' + err.message);
    }
    setMemoryLoading(false);
  };
  
  // Force Regenerate Content
  const handleForceRegenerate = async () => {
    if (!window.confirm('⚠️ ΠΡΟΣΟΧΗ!\n\nΑυτή η ενέργεια θα διαγράψει το τρέχον περιεχόμενο και θα δημιουργήσει νέο.\n\nΣυνέχεια;')) {
      return;
    }
    
    setForceRegenerateLoading(true);
    setForceRegenerateMessage(null);
    
    try {
      const token = storage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/force-regenerate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setForceRegenerateMessage({
          type: 'success',
          text: `✅ ${data.message || 'Η δημιουργία περιεχομένου ξεκίνησε!'}`
        });
      } else {
        setForceRegenerateMessage({
          type: 'error',
          text: `❌ ${data.detail || 'Σφάλμα κατά την εκκίνηση'}`
        });
      }
    } catch (err) {
      console.error('Force regenerate error:', err);
      setForceRegenerateMessage({
        type: 'error',
        text: `❌ Σφάλμα σύνδεσης: ${err.message}`
      });
    } finally {
      setForceRegenerateLoading(false);
      // Clear message after 10 seconds
      setTimeout(() => setForceRegenerateMessage(null), 10000);
    }
  };
  
  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'analyses' && analysisHistory.length === 0) {
      fetchAnalysisHistory();
    }
    if (activeTab === 'users' && userList.length === 0) {
      fetchUserList();
    }
  }, [activeTab]);
  
  // Download functions
  const downloadCSV = async (analysisId, filename) => {
    const token = storage.getItem('token');
    const response = await fetch(`${BACKEND_URL}/api/admin/analysis-history/download/csv/${analysisId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `analysis_${analysisId}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const downloadResults = async (analysisId, format = 'csv') => {
    const token = storage.getItem('token');
    const response = await fetch(`${BACKEND_URL}/api/admin/analysis-history/download/results/${analysisId}?format=${format}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `results_${analysisId}.${format}`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // v2.13.32 — Export dead-code scan report (HIGH-confidence only)
  const handleExportDeadCodeReport = async () => {
    setDeadCodeExporting(true);
    try {
      const token = storage.getItem('token');
      const target = 'services/stream_monitor_service.py';
      const response = await fetch(
        `${BACKEND_URL}/api/admin/diagnostic/dead-code-scan?target=${encodeURIComponent(target)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) {
        const errBody = await response.text();
        alert(`Dead-code scan failed (HTTP ${response.status}): ${errBody}`);
        return;
      }
      const report = await response.json();
      const blob = new Blob([JSON.stringify(report, null, 2)], {
        type: 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date()
        .toISOString()
        .replace(/[:.]/g, '-')
        .replace('T', '_')
        .replace(/Z$/, '');
      const baseName = target.split('/').pop().replace('.py', '');
      a.download = `dead-code-${baseName}-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      const totals = report.totals || {};
      alert(
        `Diagnostic Report εξήχθη επιτυχώς!\n\n` +
        `Αρχείο: ${a.download}\n\n` +
        `📊 Σύνοψη:\n` +
        `  • Total definitions: ${totals.total_definitions ?? '?'}\n` +
        `  • HIGH-confidence dead: ${totals.high_confidence_dead ?? 0}\n` +
        `  • Estimated dead lines: ${totals.estimated_dead_lines ?? 0}\n\n` +
        `Στείλε το JSON στον agent για ανάλυση.`
      );
    } catch (err) {
      alert(`Dead-code scan error: ${err.message}`);
    } finally {
      setDeadCodeExporting(false);
    }
  };
  
  // Delete analysis
  const deleteAnalysis = async (analysisId, filename) => {
    if (!window.confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε το αρχείο "${filename}";\n\nΑυτή η ενέργεια δεν μπορεί να αναιρεθεί.`)) {
      return;
    }
    
    try {
      const token = storage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/analysis-history/${analysisId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Remove from local state
        setAnalysisHistory(prev => prev.filter(a => a._id !== analysisId));
        alert('✅ Το αρχείο διαγράφηκε επιτυχώς!');
      } else {
        alert('❌ Σφάλμα κατά τη διαγραφή του αρχείου');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('❌ Σφάλμα σύνδεσης');
    }
  };

  const fetchAllStats = async () => {
    try {
      const token = storage.getItem('token');
      
      // Helper function to safely fetch with fallback
      const safeFetch = async (url, fallback = null) => {
        try {
          const res = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!res.ok) {
            // Return fallback for non-200 responses (e.g., 503 when analytics disabled)
            return fallback;
          }
          return await res.json();
        } catch {
          return fallback;
        }
      };
      
      const [daily, weekly, geo, health, users, activity] = await Promise.all([
        safeFetch(`${BACKEND_URL}/api/admin/analytics/daily`, { authenticated_visits: 0, anonymous_visits: 0 }),
        safeFetch(`${BACKEND_URL}/api/admin/analytics/weekly`, { daily_stats: [] }),
        safeFetch(`${BACKEND_URL}/api/admin/analytics/geographic`, { countries: [], cities: [] }),
        safeFetch(`${BACKEND_URL}/api/admin/dashboard/system-health`),
        safeFetch(`${BACKEND_URL}/api/admin/dashboard/user-stats`),
        safeFetch(`${BACKEND_URL}/api/admin/dashboard/recent-activity?limit=5`)
      ]);

      setDailyStats(daily);
      setWeeklyStats(weekly);
      setGeoStats(geo);
      setSystemHealth(health);
      setUserStats(users);
      setRecentActivity(activity);
      setLoading(false);
    } catch (err) {
      setError('Failed to load analytics data');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-gray-600">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  // Prepare data for charts
  const visitTypeData = dailyStats ? [
    { name: 'Authenticated', value: dailyStats.authenticated_visits, color: '#10B981' },
    { name: 'Anonymous', value: dailyStats.anonymous_visits, color: '#6366F1' }
  ] : [];

  const COLORS = ['#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#EF4444'];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-gray-800 dark:text-white mb-2">📊 Admin Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-4">Πλήρης διαχείριση συστήματος, χρηστών και εργαλείων</p>
        
        {/* Tabs - Extended with all admin tools */}
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 overflow-x-auto pb-1">
          {[
            { id: 'overview', label: '📈 Overview' },
            { id: 'users', label: '👥 Χρήστες' },
            { id: 'analyses', label: '📁 Αρχεία' },
            { id: 'scheduler', label: '⏱️ Scheduler' },
            { id: 'song-years', label: '📅 Έτη Τραγουδιών' },
            { id: 'songs-analyzer', label: '🎵 Songs Analyzer' },
            { id: 'compare-access', label: '🔗 Compare Access' },
            { id: 'song-identity', label: '🔑 Song Identity' },
            { id: 'canonical-merge', label: '🔀 Canonical Merge' },
            { id: 'canonical-conflicts', label: '⚠️ Conflicts', badge: conflictCount },
            { id: 'user-songs-db', label: '📀 User Songs DB' },
            { id: 'access-management', label: '🔓 Δωρεάν Πρόσβαση' },
            { id: 'qa', label: '✅ Έλεγχος Ποιότητας' },
            { id: 'duplicate-check', label: '🔍 Επαναλήψεις' },
            { id: 'dedup-dashboard', label: '📊 Dedup' },
            { id: 'station-trust', label: '🔒 Play Lock' },
            { id: 'rollback', label: '⏪ Rollback' },
            { id: 'stream-url-submissions', label: '🔗 Stream URLs', badge: pendingUrlSubmissions },
            { id: 'reports', label: '📊 Reports' },
            { id: 'settings', label: '⚙️ Ρυθμίσεις' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative px-3 py-2 text-sm font-semibold transition-colors whitespace-nowrap rounded-t-lg ${
                activeTab === tab.id 
                  ? 'bg-purple-600 text-white' 
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span
                  data-testid={`admin-tab-${tab.id}-badge`}
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1 leading-none"
                >
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
        {/* Maintenance Overview Widget */}
        <div className="mb-8">
          <DashboardOverviewWidget />
        </div>
        
        {/* Learning Effectiveness Widget */}
        <div className="mb-8">
          <LearningEffectivenessWidget />
        </div>
        
        {/* System Health Cards */}
        {systemHealth && (
          <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Database Status */}
            <StatCard
              icon="🗄️"
              title="Database"
              value={systemHealth.database?.status || 'N/A'}
              subtitle={systemHealth.database?.latency || 'N/A'}
              color="green"
              isStatus={true}
            />
          
          {/* Server CPU */}
          <div className="relative">
            <StatCard
              icon="💻"
              title="CPU Usage"
              value={`${systemHealth.server?.cpu_percent || 0}%`}
              subtitle={`RAM: ${systemHealth.server?.memory_percent || 0}% (${systemHealth.server?.memory_used_gb || 0}/${systemHealth.server?.memory_total_gb || 0} GB)`}
              color="blue"
            />
            <button
              onClick={fetchMemoryDiagnostics}
              disabled={memoryLoading}
              className="absolute top-2 right-2 bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded shadow"
              title="View Memory Details"
            >
              {memoryLoading ? '...' : '🔍'}
            </button>
          </div>
          
          {/* Content Status */}
          <StatCard
            icon="📝"
            title="Content"
            value={systemHealth.content_generation?.status || 'N/A'}
            subtitle={
              systemHealth.content_generation?.failed_sections?.length > 0
                ? `❌ ${systemHealth.content_generation.failed_sections.length} αποτυχίες`
                : `${systemHealth.content_generation?.total_sections || 0} sections`
            }
            color={systemHealth.content_generation?.failed_sections?.length > 0 ? 'red' : 'purple'}
            isStatus={true}
            clickable={true}
            onClick={() => setActiveTab('scheduler')}
          />
          
          {/* Failed Sections Alert */}
          {systemHealth.content_generation?.failed_sections?.length > 0 && (
            <div className="col-span-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <h4 className="font-semibold text-red-700 dark:text-red-300 mb-2">
                ⚠️ Αποτυχημένες Ενότητες Περιεχομένου
              </h4>
              <ul className="space-y-1">
                {systemHealth.content_generation.failed_sections.map((section, idx) => (
                  <li key={idx} className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                    <span>❌</span>
                    <span className="font-medium">{section.section || section}</span>
                    {section.error && (
                      <span className="text-xs text-red-500 dark:text-red-500">
                        - {section.error}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-red-500 dark:text-red-400">
                Πατήστε στο &quot;Scheduler&quot; tab για περισσότερες λεπτομέρειες
              </p>
            </div>
          )}
          
          {/* Radio Chart */}
          <StatCard
            icon="🎵"
            title="Radio Chart"
            value={`${systemHealth.radio_chart?.total_songs || 0}`}
            subtitle={`${systemHealth.radio_chart?.greek_songs || 0} Greek / ${systemHealth.radio_chart?.international_songs || 0} Intl`}
            color="pink"
          />
        </div>
      )}

      {/* User Stats Cards */}
      {userStats && (
        <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon="👥"
            title="Total Users"
            value={userStats.total_users || 0}
            subtitle={`${userStats.active_today || 0} active today`}
            color="teal"
            clickable={true}
            onClick={() => fetchUsersForModal('all', '👥 Όλοι οι Χρήστες')}
          />
          
          <StatCard
            icon="🔐"
            title="2FA Status"
            value={`${userStats.two_factor?.enabled || 0}`}
            subtitle={`${userStats.two_factor?.disabled || 0} disabled`}
            color="orange"
            clickable={true}
            onClick={() => fetchUsersForModal('2fa_enabled', '🔐 Χρήστες με 2FA')}
          />
          
          <StatCard
            icon="✨"
            title="New Signups"
            value={userStats.new_signups_7d || 0}
            subtitle="Last 7 days"
            color="indigo"
            clickable={true}
            onClick={() => fetchUsersForModal('new_signups', '✨ Νέες Εγγραφές (7 ημέρες)')}
          />
          
          <StatCard
            icon="👑"
            title="Admins"
            value={userStats.roles?.admin || 0}
            subtitle={`${userStats.roles?.user || 0} regular users`}
            color="red"
            clickable={true}
            onClick={() => fetchUsersForModal('admins', '👑 Administrators')}
          />
        </div>
      )}

      {/* Admin Quick Actions */}
      <div className="mb-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">🛠️ Admin Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <a 
            href="/admin/radio-chart-upload"
            className="flex items-center p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg"
          >
            <span className="text-3xl mr-3">📻</span>
            <div>
              <h3 className="font-bold text-lg">Radio Top Songs</h3>
              <p className="text-sm text-blue-100">Upload CSV Chart</p>
            </div>
          </a>
          <a 
            href="/radio-top-songs"
            className="flex items-center p-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
          >
            <span className="text-3xl mr-3">🎵</span>
            <div>
              <h3 className="font-bold text-lg">View Radio Chart</h3>
              <p className="text-sm text-purple-100">Public Display</p>
            </div>
          </a>
          <a 
            href="/admin/stream-monitor"
            className="flex items-center p-4 bg-gradient-to-br from-cyan-500 to-cyan-600 text-white rounded-lg hover:from-cyan-600 hover:to-cyan-700 transition-all shadow-md hover:shadow-lg"
          >
            <span className="text-3xl mr-3">📡</span>
            <div>
              <h3 className="font-bold text-lg">Stream Monitor</h3>
              <p className="text-sm text-cyan-100">Real-time Streaming Analytics</p>
            </div>
          </a>
          <a
            href="/admin/ranking-diagnostics"
            data-testid="ranking-diagnostics-link"
            className="flex items-center p-4 bg-gradient-to-br from-violet-500 to-violet-700 text-white rounded-lg hover:from-violet-600 hover:to-violet-800 transition-all shadow-md hover:shadow-lg"
          >
            <span className="text-3xl mr-3">🧠</span>
            <div>
              <h3 className="font-bold text-lg">Ranking Diagnostics</h3>
              <p className="text-sm text-violet-100">Anchor calibration · learning progress</p>
            </div>
          </a>
          <a
            href="/admin/layered-telemetry"
            data-testid="layered-telemetry-link"
            className="flex items-center p-4 bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all shadow-md hover:shadow-lg"
          >
            <span className="text-3xl mr-3">📊</span>
            <div>
              <h3 className="font-bold text-lg">Layered Telemetry</h3>
              <p className="text-sm text-emerald-100">Κορεσμός pipeline · semaphores · listener pool</p>
            </div>
          </a>
          <a
            href="/admin/c1-diagnostics"
            data-testid="c1-diagnostics-link"
            className="flex items-center p-4 bg-gradient-to-br from-cyan-500 to-sky-600 text-white rounded-lg hover:from-cyan-600 hover:to-sky-700 transition-all shadow-md hover:shadow-lg"
          >
            <span className="text-3xl mr-3">🎯</span>
            <div>
              <h3 className="font-bold text-lg">C1 Grace Margin Diagnostics</h3>
              <p className="text-sm text-cyan-100">Live Shazam pacer · grace admits · JSON export</p>
            </div>
          </a>
          <a
            href="/admin/email-unsubscribers"
            data-testid="email-unsubscribers-link"
            className="flex items-center p-4 bg-gradient-to-br from-rose-500 to-pink-600 text-white rounded-lg hover:from-rose-600 hover:to-pink-700 transition-all shadow-md hover:shadow-lg"
          >
            <span className="text-3xl mr-3">📧</span>
            <div>
              <h3 className="font-bold text-lg">Email Unsubscribers</h3>
              <p className="text-sm text-rose-100">Manage re-engagement opt-outs · send test emails</p>
            </div>
          </a>
          <a
            href="/admin/classifier-report"
            data-testid="classifier-report-link"
            className="flex items-center p-4 bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all shadow-md hover:shadow-lg"
          >
            <span className="text-3xl mr-3">🎙️</span>
            <div>
              <h3 className="font-bold text-lg">Classifier Report</h3>
              <p className="text-sm text-amber-100">inaSpeechSegmenter · gating savings · CSV/JSON export</p>
            </div>
          </a>
          <a
            href="/admin/break-intelligence"
            data-testid="break-intelligence-link"
            className="flex items-center p-4 bg-gradient-to-br from-purple-600 to-fuchsia-700 text-white rounded-lg hover:from-purple-700 hover:to-fuchsia-800 transition-all shadow-md hover:shadow-lg"
          >
            <span className="text-3xl mr-3">🧠</span>
            <div>
              <h3 className="font-bold text-lg">Break Intelligence</h3>
              <p className="text-sm text-purple-100">station learning profiles · minute heatmap · confidence weights</p>
            </div>
          </a>
          {/* v2.13.32 — Dead Code Scanner Export */}
          <button
            onClick={handleExportDeadCodeReport}
            disabled={deadCodeExporting}
            data-testid="export-dead-code-report-btn"
            className="flex items-center p-4 bg-gradient-to-br from-slate-700 to-slate-900 text-white rounded-lg hover:from-slate-800 hover:to-black transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-left"
          >
            <span className="text-3xl mr-3">📋</span>
            <div>
              <h3 className="font-bold text-lg">
                {deadCodeExporting ? 'Generating…' : 'Export Diagnostic Report'}
              </h3>
              <p className="text-sm text-slate-300">
                dead code scan · stream_monitor_service.py · download JSON
              </p>
            </div>
          </button>
          {/* Force Regenerate Button */}
          <button
            onClick={handleForceRegenerate}
            disabled={forceRegenerateLoading}
            data-testid="force-regenerate-btn"
            className="flex items-center p-4 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-3xl mr-3">{forceRegenerateLoading ? '⏳' : '🔄'}</span>
            <div>
              <h3 className="font-bold text-lg">Force Regenerate</h3>
              <p className="text-sm text-red-100">
                {forceRegenerateLoading ? 'Δημιουργία...' : 'Αναδημιουργία περιεχομένου'}
              </p>
            </div>
          </button>
          {/* Weekly Digest Test Send */}
          <button
            onClick={async () => {
              const email = prompt('Email παραλήπτη για το test digest:', 'spirosballas@gmail.com');
              if (!email) return;
              try {
                const API = process.env.REACT_APP_BACKEND_URL;
                const token = localStorage.getItem('token');
                const r = await fetch(`${API}/api/songs-analyzer/digest/test?email=${encodeURIComponent(email)}`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                });
                const d = await r.json();
                if (d.success) {
                  alert(`✅ Στάλθηκε σε ${d.email_to} με ${d.stations_count} σταθμούς.\nΘέμα: ${d.subject}`);
                } else {
                  alert(`❌ Αποτυχία: ${d.detail || d.error || 'unknown'}`);
                }
              } catch (e) {
                alert(`❌ Σφάλμα: ${e.message}`);
              }
            }}
            data-testid="digest-test-btn"
            className="flex items-center p-4 bg-gradient-to-br from-indigo-500 to-indigo-700 text-white rounded-lg hover:from-indigo-600 hover:to-indigo-800 transition-all shadow-md hover:shadow-lg"
          >
            <span className="text-3xl mr-3">📧</span>
            <div>
              <h3 className="font-bold text-lg">Test Weekly Digest</h3>
              <p className="text-sm text-indigo-100">Στείλε test email (όλοι οι σταθμοί σε ένα)</p>
            </div>
          </button>
          {/* CUME Tracker — run now + coverage check */}
          <button
            onClick={async () => {
              try {
                const API = process.env.REACT_APP_BACKEND_URL;
                const token = localStorage.getItem('token');
                const r = await fetch(`${API}/api/songs-analyzer/cume/track-now`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                });
                const d = await r.json();
                const cov = await fetch(`${API}/api/songs-analyzer/cume/coverage`, {
                  headers: { Authorization: `Bearer ${token}` },
                }).then(x => x.json());
                const withCume = cov.stations?.filter(s => s.has_cume) || [];
                alert(
                  `Tracker ran: ${d.tracked} σταθμοί ελέγχθηκαν\n` +
                  `Με ακριβές CUME counter: ${d.with_counter}\n\n` +
                  `Συνολική κάλυψη (τελ. 7 ημέρες):\n` +
                  `${cov.stations_with_cume}/${cov.total_stations} σταθμοί (${cov.coverage_pct}%)\n\n` +
                  `Δίνουν ακριβή δεδομένα:\n` +
                  withCume.slice(0, 10).map(s => `• ${s.name} (${s.source})`).join('\n') +
                  (withCume.length > 10 ? `\n...και ${withCume.length - 10} ακόμα` : '')
                );
              } catch (e) {
                alert(`❌ Σφάλμα: ${e.message}`);
              }
            }}
            data-testid="cume-track-btn"
            className="flex items-center p-4 bg-gradient-to-br from-teal-500 to-teal-700 text-white rounded-lg hover:from-teal-600 hover:to-teal-800 transition-all shadow-md hover:shadow-lg"
          >
            <span className="text-3xl mr-3">🎯</span>
            <div>
              <h3 className="font-bold text-lg">CUME Tracker</h3>
              <p className="text-sm text-teal-100">Ακριβείς νέοι ακροατές/ώρα — τρέξε & δες κάλυψη</p>
            </div>
          </button>
        </div>
        
        {/* Force Regenerate Message */}
        {forceRegenerateMessage && (
          <div className={`mt-4 p-4 rounded-lg ${
            forceRegenerateMessage.type === 'success' 
              ? 'bg-green-100 border border-green-400 text-green-700' 
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            {forceRegenerateMessage.text}
          </div>
        )}
      </div>

      {/* Today's Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <StatCard 
          icon="👥"
          title="Total Visits" 
          value={dailyStats?.total_visits || 0} 
          subtitle="Today's total visits"
          color="purple"
          clickable={true}
          onClick={() => fetchUsersForModal('visitors_today', '👥 Επισκέπτες Σήμερα')}
        />
        <StatCard 
          icon="🌐"
          title="Unique Visitors" 
          value={dailyStats?.unique_visitors || 0} 
          subtitle="Unique visitors today"
          color="blue"
          clickable={true}
          onClick={() => fetchUsersForModal('unique_visitors', '🌐 Μοναδικοί Επισκέπτες')}
        />
        <StatCard 
          icon="🔐"
          title="Authenticated" 
          value={dailyStats?.authenticated_visits || 0} 
          subtitle="Logged in users"
          color="green"
          clickable={true}
          onClick={() => fetchUsersForModal('authenticated', '🔐 Συνδεδεμένοι Χρήστες')}
        />
        <StatCard 
          icon="👤"
          title="Anonymous" 
          value={dailyStats?.anonymous_visits || 0} 
          subtitle="Guest visitors"
          color="indigo"
        />
        <StatCard 
          icon="🔑"
          title="Logins" 
          value={dailyStats?.total_logins || 0} 
          subtitle="Login attempts today"
          color="pink"
          clickable={true}
          onClick={() => fetchUsersForModal('logins_today', '🔑 Logins Σήμερα')}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Weekly Trend */}
        <ChartCard title="📈 Weekly Trend">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={weeklyStats?.daily_breakdown || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="visits" stroke="#8B5CF6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              7-Day Average: <span className="font-bold text-purple-600">{weeklyStats?.daily_average || 0}</span> visits/day
            </p>
          </div>
        </ChartCard>

        {/* Visit Type Distribution */}
        <ChartCard title="🔐 Visit Type Distribution">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={visitTypeData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {visitTypeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts Row 2 - Geographic */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Countries */}
        <ChartCard title="🌍 Top Countries">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={geoStats?.top_countries || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="country" type="category" width={100} />
              <Tooltip />
              <Bar dataKey="visits" fill="#8B5CF6" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Top Cities */}
        <ChartCard title="🏙️ Top Cities">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={geoStats?.top_cities || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="city" type="category" width={100} />
              <Tooltip />
              <Bar dataKey="visits" fill="#EC4899" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Refresh Info */}
      <div className="mt-6 text-center text-sm text-gray-500">
        Auto-refreshes every 30 seconds • Last updated: {new Date().toLocaleTimeString()}
      </div>
      </>
      )}
      
      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">👥 Εγγεγραμμένοι Χρήστες</h2>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              {/* Search Input */}
              <div className="relative flex-1 sm:w-64">
                <input
                  type="text"
                  placeholder="🔍 Αναζήτηση username, email, station..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
                {userSearch && (
                  <button
                    onClick={() => setUserSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
              <button 
                onClick={fetchUserList}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
          
          {usersLoading ? (
            <div className="text-center py-8 text-gray-500">Loading users...</div>
          ) : (
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Username</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Station</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Last Login</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredUsers.map((user, index) => (
                    <tr key={user._id || index} className={index < 15 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 font-medium">{index + 1}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">{user.username}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{user.email || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{user.station_name || '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          user.role === 'admin' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                        }`}>
                          {user.role || 'user'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('el-GR') : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {user.last_login ? new Date(user.last_login).toLocaleString('el-GR') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredUsers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  {userSearch ? `Δεν βρέθηκαν χρήστες για "${userSearch}"` : 'No users found'}
                </div>
              )}
            </div>
          )}
          <div className="mt-4 text-sm text-gray-500 text-right">
            {userSearch ? `Εμφάνιση: ${filteredUsers.length} / ` : ''}Total: {userList.length} users
          </div>
        </div>
      )}
      
      {/* Analysis History Tab */}
      {activeTab === 'analyses' && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">📁 Αποθηκευμένα Αρχεία Χρηστών</h2>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              {/* Bulk Delete Button */}
              {selectedAnalyses.size > 0 && (
                <button
                  onClick={bulkDeleteAnalyses}
                  className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 flex items-center gap-2"
                >
                  🗑️ Διαγραφή ({selectedAnalyses.size})
                </button>
              )}
              {/* Search Input */}
              <div className="relative flex-1 sm:w-64">
                <input
                  type="text"
                  placeholder="🔍 Αναζήτηση user, station, file..."
                  value={analysisSearch}
                  onChange={(e) => setAnalysisSearch(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
                {analysisSearch && (
                  <button
                    onClick={() => setAnalysisSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
              <button 
                onClick={fetchAnalysisHistory}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
          
          {analysisLoading ? (
            <div className="text-center py-8 text-gray-500">Loading analyses...</div>
          ) : (
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-gray-100">
                  <tr>
                    <th className="px-2 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={filteredAnalyses.length > 0 && selectedAnalyses.size === filteredAnalyses.length}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                        title="Επιλογή όλων"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Τύπος</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Χρήστης</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Σταθμός</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Αρχείο</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Μέγεθος</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Ημ/νία</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Ενέργειες</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAnalyses.map((analysis, index) => (
                    <tr key={analysis._id || index} className={`${index < 15 ? 'bg-white' : 'bg-gray-50'} ${selectedAnalyses.has(analysis._id) ? 'bg-purple-50' : ''}`}>
                      <td className="px-2 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedAnalyses.has(analysis._id)}
                          onChange={() => toggleSelectAnalysis(analysis._id)}
                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-medium">{index + 1}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          analysis.analyzer_type === 'daypart' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {analysis.analyzer_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">
                        {analysis.username || analysis.email || 'Anonymous'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{analysis.station_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{analysis.csv_filename || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {analysis.csv_size_kb ? `${analysis.csv_size_kb.toFixed(1)} KB` : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {analysis.created_at ? new Date(analysis.created_at).toLocaleString('el-GR') : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm space-x-2">
                        <button
                          onClick={() => downloadCSV(analysis._id, analysis.csv_filename)}
                          className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                          title="Download original CSV"
                        >
                          📄 CSV
                        </button>
                        <button
                          onClick={() => downloadResults(analysis._id, 'csv')}
                          className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                          title="Download analysis results"
                        >
                          📊 Results
                        </button>
                        <button
                          onClick={() => deleteAnalysis(analysis._id, analysis.csv_filename)}
                          className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                          title="Διαγραφή αρχείου"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredAnalyses.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  {analysisSearch ? `Δεν βρέθηκαν αναλύσεις για "${analysisSearch}"` : 'Δεν υπάρχουν αποθηκευμένα αρχεία. Τα αρχεία αποθηκεύονται αυτόματα όταν οι χρήστες χρησιμοποιούν τους Analyzers.'}
                </div>
              )}
            </div>
          )}
          <div className="mt-4 text-sm text-gray-500 text-right">
            {analysisSearch ? `Εμφάνιση: ${filteredAnalyses.length} / ` : ''}Σύνολο: {analysisHistory.length} αρχεία • Αυτόματη διαγραφή μετά από 365 ημέρες
          </div>
        </div>
      )}
      
      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <AdminConfigSettings />
          </div>

          {/* Stations Sync — backup + restore station configs across environments
              (moved here from "Έτη Τραγουδιών" tab — unrelated to release years) */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <StationsSyncPanel />
          </div>

          {/* Recent Records Cleanup — RETIRED 2026-05-16 (v2.6.4).
              Confidence Gate v1.6 rejects jingles/short tracks/duplicates at
              ingestion, so retroactive cleanup is no longer needed and would
              second-guess the gate. The component definition below is kept as
              dead code for emergency rollback. */}
        </div>
      )}
      
      {/* Songs Analyzer Access Tab */}
      {activeTab === 'songs-analyzer' && (
        <AdminSongsAnalyzerAccess />
      )}
      
      {/* Access Management Tab - Δωρεάν Πρόσβαση */}
      {activeTab === 'access-management' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <AdminAccessManagement />
        </div>
      )}
      
      {/* Content QA Tab - Έλεγχος Ποιότητας */}
      {activeTab === 'qa' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <ContentQA />
        </div>
      )}
      
      {/* Duplicate Checker Tab - Έλεγχος Επαναλήψεων */}
      {activeTab === 'duplicate-check' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <DuplicateChecker />
        </div>
      )}
      
      {/* Dedup Dashboard Tab */}
      {activeTab === 'dedup-dashboard' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <DedupDashboard />
        </div>
      )}
      
      {/* Station Trust Dashboard Tab */}
      {activeTab === 'station-trust' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <StationTrustDashboard />
        </div>
      )}
      
      {/* Rollback Manager Tab */}
      {activeTab === 'rollback' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <RollbackManager />
        </div>
      )}

      {/* Stream URL Submissions Tab */}
      {activeTab === 'stream-url-submissions' && (
        <StreamUrlSubmissionsPanel />
      )}

      {/* Reports & Exports Tab */}
      {activeTab === 'reports' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 space-y-6">
          <LLMDedupReportPanel token={localStorage.getItem('token')} />
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <DuplicateCleanupPanel token={localStorage.getItem('token')} />
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <AdminMaintenanceTools token={localStorage.getItem('token')} />
          </div>
        </div>
      )}
      
      {/* Scheduler Status Tab */}
      {activeTab === 'scheduler' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <SchedulerStatus />
        </div>
      )}
      
      {/* Song Year Manager Tab */}
      {activeTab === 'song-years' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <SongYearManager />
          </div>
        </div>
      )}
      
      {/* Song Identity Manager Tab */}
      {activeTab === 'song-identity' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <BadTrackEraser />
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <SongIdentityManager />
          </div>
        </div>
      )}
      
      {/* Compare Stations Access Tab */}
      {activeTab === 'compare-access' && (
        <div className="bg-gray-900 rounded-lg shadow-lg p-6">
          <AdminCompareStationsAccess />
        </div>
      )}
      
      {/* Canonical Merge Job Tab */}
      {activeTab === 'canonical-merge' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <CanonicalMergeJob />
        </div>
      )}
      
      {/* Canonical Conflicts Tab - ZERO-LOSS Resolution */}
      {activeTab === 'canonical-conflicts' && (
        <div className="bg-gray-900 rounded-lg shadow-lg">
          <CanonicalConflictResolver />
        </div>
      )}
      
      {/* User Songs DB Tab - Verified Fingerprint Database */}
      {activeTab === 'user-songs-db' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <UserSongsDatabase />
        </div>
      )}
      
      {/* Users Modal */}
      {showUsersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-800">{modalTitle}</h3>
              <button 
                onClick={() => setShowUsersModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            
            {modalLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : modalUsers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Station</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {modalUsers.map((user, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {user.username || 'Anonymous'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {user.email || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {user.station_name || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {user.timestamp ? new Date(user.timestamp).toLocaleString('el-GR') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Δεν βρέθηκαν χρήστες
              </div>
            )}
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowUsersModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Κλείσιμο
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Memory Diagnostics Modal */}
      <MemoryDiagnosticsModal 
        show={showMemoryModal}
        onClose={() => setShowMemoryModal(false)}
        data={memoryDiagnostics}
      />
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, title, value, subtitle, color, isStatus, onClick, clickable }) => {
  // Status badge styling
  const getStatusColor = (status) => {
    if (typeof status === 'string') {
      const s = status.toLowerCase();
      if (s.includes('healthy') || s.includes('up_to_date') || s.includes('active')) return 'bg-green-100 text-green-800';
      if (s.includes('unhealthy') || s.includes('outdated') || s.includes('error')) return 'bg-red-100 text-red-800';
      return 'bg-yellow-100 text-yellow-800';
    }
    return '';
  };

  return (
    <div 
      className={`bg-gradient-to-br from-${color}-500 to-${color}-600 rounded-lg p-6 text-white shadow-md hover:shadow-xl transition-shadow ${clickable ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`}
      onClick={clickable ? onClick : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-3xl">{icon}</span>
        {isStatus ? (
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(value)}`}>
            {typeof value === 'string' ? value.toUpperCase() : value}
          </span>
        ) : (
          <span className="text-4xl font-bold">{value}</span>
        )}
      </div>
      <h3 className="text-sm font-semibold mb-1">{title}</h3>
      <p className="text-xs text-opacity-80">{subtitle}</p>
      {clickable && <p className="text-xs mt-2 opacity-70">👆 Click to view details</p>}
    </div>
  );
};

// Chart Card Component
const ChartCard = ({ title, children }) => (
  <div className="bg-white rounded-lg shadow-lg p-6">
    <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4">{title}</h3>
    {children}
  </div>
);

// Memory Diagnostics Modal Component
const MemoryDiagnosticsModal = ({ show, onClose, data }) => {
  if (!show || !data) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">🔍 Memory Diagnostics</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* System Memory */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-bold text-blue-800 mb-2">💾 System Memory</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Total:</span>
                <span className="font-bold ml-2">{data.system_memory?.total_gb} GB</span>
              </div>
              <div>
                <span className="text-gray-600">Used:</span>
                <span className="font-bold ml-2">{data.system_memory?.used_gb} GB</span>
              </div>
              <div>
                <span className="text-gray-600">Available:</span>
                <span className="font-bold ml-2">{data.system_memory?.available_gb} GB</span>
              </div>
              <div>
                <span className="text-gray-600">Usage:</span>
                <span className={`font-bold ml-2 ${data.system_memory?.percent > 90 ? 'text-red-600' : data.system_memory?.percent > 70 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {data.system_memory?.percent}%
                </span>
              </div>
            </div>
          </div>
          
          {/* Backend Process */}
          <div className="bg-purple-50 rounded-lg p-4">
            <h3 className="font-bold text-purple-800 mb-2">🐍 Backend Process</h3>
            <div className="text-sm">
              <span className="text-gray-600">RSS Memory:</span>
              <span className="font-bold ml-2">{data.process_memory?.rss_mb} MB</span>
              <span className="text-gray-400 ml-4">VMS:</span>
              <span className="font-bold ml-2">{data.process_memory?.vms_mb} MB</span>
            </div>
          </div>
          
          {/* All Processes */}
          {data.all_processes && data.all_processes.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-bold text-gray-800 mb-3">📊 Top Processes by Memory</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="px-3 py-2 text-left">Process</th>
                      <th className="px-3 py-2 text-right">Memory (MB)</th>
                      <th className="px-3 py-2 text-right">CPU %</th>
                      <th className="px-3 py-2 text-right">PID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.all_processes.map((proc, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 font-medium">{proc.name}</td>
                        <td className="px-3 py-2 text-right font-mono">
                          <span className={proc.memory_mb > 500 ? 'text-red-600 font-bold' : proc.memory_mb > 200 ? 'text-yellow-600' : ''}>
                            {proc.memory_mb}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{proc.cpu_percent || 0}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-500">{proc.pid}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Known Consumers */}
          {data.known_consumers && (
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="font-bold text-green-800 mb-2">📦 Known Memory Consumers</h3>
              <div className="text-sm space-y-1">
                {Object.entries(data.known_consumers).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-gray-600">{key}:</span>
                    <span className="font-mono">
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Timestamp */}
          <div className="text-xs text-gray-500 text-right">
            Updated: {data.timestamp ? new Date(data.timestamp).toLocaleString('el-GR') : 'N/A'}
          </div>
        </div>
        
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Recent Records Cleanup Component
const RecentRecordsCleanup = () => {
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [days, setDays] = useState(2);
  const [message, setMessage] = useState(null);

  const getAuthHeaders = () => {
    const token = storage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const handlePreview = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/system/cleanup-recent-records?days=${days}&dry_run=true`,
        { method: 'POST', ...getAuthHeaders() }
      );
      const data = await res.json();
      if (data.success) {
        setPreviewData(data.result);
        if (data.result?.total_removed > 0) {
          setShowConfirm(true);
        } else {
          setMessage({ type: 'success', text: 'Δεν βρέθηκαν λανθασμένες καταγραφές!' });
        }
      } else {
        setMessage({ type: 'error', text: data.error || 'Σφάλμα' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/system/cleanup-recent-records?days=${days}&dry_run=false`,
        { method: 'POST', ...getAuthHeaders() }
      );
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: `✅ Διαγράφηκαν ${data.result?.total_removed || 0} λανθασμένες εγγραφές!` });
        setShowConfirm(false);
        setPreviewData(null);
      } else {
        setMessage({ type: 'error', text: data.error || 'Σφάλμα' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
        🧹 Καθαρισμός Πρόσφατων Εγγραφών
      </h2>
      
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Αφαίρεση λανθασμένων καταγραφών (duplicates, short tracks &lt;60s, jingles).
        Χρήσιμο για να διορθωθούν τα δεδομένα πριν κλείσει η εβδομάδα.
      </p>
      
      <div className="flex items-center gap-4 mb-4">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Ημέρες:</label>
        <select 
          value={days} 
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="px-3 py-2 border rounded-md text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value={2}>2 (Σήμερα + Χθες)</option>
          <option value={3}>3 ημέρες</option>
          <option value={7}>7 ημέρες (Εβδομάδα)</option>
        </select>
        <button
          onClick={handlePreview}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? (
            <span className="animate-spin">⏳</span>
          ) : (
            <span>🔍</span>
          )}
          Προεπισκόπηση
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-md mb-4 ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirm && previewData && (
        <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">⚠️</span>
            <span className="font-medium text-blue-700 dark:text-blue-300">
              Βρέθηκαν {previewData.total_removed} λανθασμένες εγγραφές
            </span>
          </div>
          
          <div className="mb-4 space-y-1 text-sm text-gray-600 dark:text-gray-400">
            <p>• Duplicates: <span className="font-medium">{previewData.duplicates_removed}</span></p>
            <p>• Short tracks (&lt;60s): <span className="font-medium">{previewData.short_tracks_removed}</span></p>
            <p>• Jingles/Station IDs: <span className="font-medium">{previewData.jingles_removed}</span></p>
            <p>• Invalid records: <span className="font-medium">{previewData.invalid_removed}</span></p>
            <p>• Audio metadata/HTML entities: <span className="font-medium">{previewData.metadata_cleaned || 0}</span></p>
            <hr className="my-2 border-gray-300" />
            <p>Εγγραφές που θα διαγραφούν: <span className="font-medium text-red-600">{previewData.total_removed}</span></p>
            <p>Εγγραφές που θα καθαριστούν: <span className="font-medium text-blue-600">{previewData.metadata_cleaned || 0}</span></p>
            <p>Εγγραφές που θα παραμείνουν: <span className="font-medium text-green-600">{previewData.remaining_records}</span></p>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={handleExecute}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <span className="animate-spin">⏳</span> : <span>✅</span>}
              Εκτέλεση Καθαρισμού
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              disabled={loading}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Ακύρωση
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
